import { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRcsTemplatesList, useQuickSendRcs } from '@/hooks/useRcs';
import type { RcsTemplate } from '@/types/api.types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const E164_RE = /^\+[1-9]\d{7,14}$/;

function getVariables(template: RcsTemplate): string[] {
  if (!template.variables) return [];
  if (Array.isArray(template.variables)) return template.variables as string[];
  return [];
}

function getTemplateId(template: RcsTemplate): string {
  const c = template.content as Record<string, unknown>;
  return typeof c?.templateId === 'string' ? c.templateId : template.name;
}

export function RcsQuickSendDialog({ open, onOpenChange }: Props) {
  const { data: tplData } = useRcsTemplatesList({ isActive: true, type: 'template' });
  const templates = tplData?.data ?? [];

  const { mutate: send, isPending } = useQuickSendRcs();

  const [templateId, setTemplateId] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedTemplate = templates.find((t) => t.id === templateId) ?? null;
  const variables = selectedTemplate ? getVariables(selectedTemplate) : [];

  useEffect(() => {
    setFields({});
  }, [templateId]);

  useEffect(() => {
    if (open) {
      setTemplateId('');
      setPhone('');
      setName('');
      setFields({});
      setErrors({});
    }
  }, [open]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!templateId) errs.template = 'Selecione um template';
    if (!phone) errs.phone = 'Telefone é obrigatório';
    else if (!E164_RE.test(phone)) errs.phone = 'Formato inválido. Use E.164: +5511999990000';
    for (const v of variables) {
      if (!fields[v]?.trim()) errs[v] = `Campo "${v}" é obrigatório`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate() || !selectedTemplate) return;

    const payload = {
      type: 'template' as const,
      templateId: getTemplateId(selectedTemplate),
      fields,
    };

    send(
      { contactPhone: phone, contactName: name.trim() || undefined, payload },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Envio rápido RCS
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label>Template *</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className={errors.template ? 'border-destructive' : ''}>
                <SelectValue placeholder="Selecionar template…" />
              </SelectTrigger>
              <SelectContent>
                {templates.length === 0 && (
                  <SelectItem value="__none__" disabled>
                    Nenhum template RCS ativo
                  </SelectItem>
                )}
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.template && <p className="text-xs text-destructive">{errors.template}</p>}
          </div>

          {variables.length > 0 && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Variáveis do template
              </p>
              {variables.map((v) => (
                <div key={v} className="space-y-1.5">
                  <Label htmlFor={`rcs-qs-var-${v}`}>{v} *</Label>
                  <Input
                    id={`rcs-qs-var-${v}`}
                    placeholder={`Valor para {{${v}}}`}
                    value={fields[v] ?? ''}
                    onChange={(e) => setFields((f) => ({ ...f, [v]: e.target.value }))}
                    className={errors[v] ? 'border-destructive' : ''}
                  />
                  {errors[v] && <p className="text-xs text-destructive">{errors[v]}</p>}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="rcs-qs-phone">
              Telefone * <span className="text-muted-foreground font-normal">(E.164)</span>
            </Label>
            <Input
              id="rcs-qs-phone"
              placeholder="+5511999990000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={errors.phone ? 'border-destructive' : ''}
            />
            {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rcs-qs-name">
              Nome do contato <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input
              id="rcs-qs-name"
              placeholder="João da Silva"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {isPending ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
